import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  LinkedIn as LinkedInIcon,
  Web as WebIcon,
  GetApp as GetAppIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Candidate, EmailComposerData } from '../../types';
import airtableService from '../../services/airtable';
import emailService from '../../services/emailService';
import EmailComposer from '../../components/EmailComposer';

const CandidateProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchCandidate(id);
    }
  }, [id]);

  const fetchCandidate = async (candidateId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Since we don't have a getCandidateById method, we'll fetch all and filter
      const response = await airtableService.getCandidates();

      if (response.success) {
        const foundCandidate = response.data.find(c => c.id === candidateId);
        if (foundCandidate) {
          setCandidate(foundCandidate);
        } else {
          setError('Candidate not found');
        }
      } else {
        setError(response.error || 'Failed to fetch candidate');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch candidate');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'info';
      case 'Screening': return 'warning';
      case 'In Process': return 'primary';
      case 'Hired': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const handleSendEmail = async (emailData: EmailComposerData) => {
    try {
      // Add to email queue
      const queueResponse = await emailService.addToQueue(emailData, 'current-user-id');
      if (queueResponse.success) {
        console.log('✅ Email queued successfully');
      } else {
        console.error('❌ Failed to queue email:', queueResponse.error);
        throw new Error(queueResponse.error);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !candidate) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Candidate not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/candidates')}>
          Back to Candidates
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/candidates')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Candidate Profile
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Basic Info */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main'
                }}
                src={candidate.candidateImage?.[0]?.thumbnails?.large?.url || candidate.candidateImage?.[0]?.url}
              >
                {!candidate.candidateImage?.length && <PersonIcon sx={{ fontSize: 60 }} />}
              </Avatar>

              <Typography variant="h5" gutterBottom>
                {candidate.firstName} {candidate.lastName}
              </Typography>

              <Typography variant="h6" color="text.secondary" gutterBottom>
                {candidate.currentJobTitle}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                at {candidate.currentCompany}
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Chip
                  label={candidate.status}
                  color={getStatusColor(candidate.status) as any}
                  size="medium"
                />
              </Box>

              {/* Action Buttons */}
              <Box display="flex" flexDirection="column" gap={1}>
                {candidate.resumeAttachment?.[0] && (
                  <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    onClick={() => window.open(candidate.resumeAttachment![0].url, '_blank')}
                    fullWidth
                  >
                    Download Resume
                  </Button>
                )}

                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={() => setEmailComposerOpen(true)}
                  fullWidth
                >
                  Send Email
                </Button>

                {candidate.phone && (
                  <Button
                    variant="outlined"
                    startIcon={<PhoneIcon />}
                    href={`tel:${candidate.phone}`}
                    fullWidth
                  >
                    Call
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card elevation={2} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary={candidate.email} />
                </ListItem>

                {candidate.phone && (
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText primary={candidate.phone} />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon>
                    <LocationIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={candidate.currentLocation}
                    secondary={candidate.willingToRelocate ? 'Open to relocation' : 'Not open to relocation'}
                  />
                </ListItem>

                {candidate.linkedinProfile && (
                  <ListItem>
                    <ListItemIcon>
                      <LinkedInIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <a
                          href={candidate.linkedinProfile}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#00bcd4',
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                            e.currentTarget.style.color = '#0097a7';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                            e.currentTarget.style.color = '#00bcd4';
                          }}
                        >
                          LinkedIn Profile
                        </a>
                      }
                    />
                  </ListItem>
                )}

                {candidate.portfolioUrl && (
                  <ListItem>
                    <ListItemIcon>
                      <WebIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <a
                          href={candidate.portfolioUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#00bcd4',
                            textDecoration: 'none',
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = 'underline';
                            e.currentTarget.style.color = '#0097a7';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = 'none';
                            e.currentTarget.style.color = '#00bcd4';
                          }}
                        >
                          Portfolio
                        </a>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Detailed Information */}
        <Grid item xs={12} md={8}>
          {/* Professional Information */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Professional Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Current Position
                      </Typography>
                      <Typography variant="body1">
                        {candidate.currentJobTitle}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Experience
                      </Typography>
                      <Typography variant="body1">
                        {candidate.yearsOfExperience} years
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Salary Expectation
                      </Typography>
                      <Typography variant="body1">
                        ${candidate.salaryExpectation?.toLocaleString() || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Notice Period
                      </Typography>
                      <Typography variant="body1">
                        {candidate.noticePeriod}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Source
                    </Typography>
                    <Typography variant="body1">
                      {candidate.source}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Notes */}
          {candidate.notes && (
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {candidate.notes}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Resume Attachment */}
          {candidate.resumeAttachment?.length && (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resume & Documents
                </Typography>
                {candidate.resumeAttachment.map((attachment) => (
                  <Box key={attachment.id} display="flex" alignItems="center" justifyContent="space-between" p={1} border="1px solid" borderColor="divider" borderRadius={1} mb={1}>
                    <Box>
                      <Typography variant="body1">
                        {attachment.filename}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(attachment.size / 1024)} KB • {attachment.type}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetAppIcon />}
                      onClick={() => window.open(attachment.url, '_blank')}
                    >
                      Download
                    </Button>
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Email Composer */}
      <EmailComposer
        open={emailComposerOpen}
        onClose={() => setEmailComposerOpen(false)}
        recipients={candidate ? [candidate] : []}
        onSend={handleSendEmail}
      />
    </Box>
  );
};

export default CandidateProfile;